import React, { FunctionComponent } from 'react';
import withStyles from 'react-jss';
import { useLocation } from 'react-router-dom';
import { Grid, Header } from 'semantic-ui-react';
import queryString from 'query-string';
import { format } from 'd3-format';
import { isEmpty } from 'ramda';

import Query from 'scenes/Report/components/Query';
import ReportBase, { ReportParams } from 'scenes/Report/components/ReportBase';
import { ClassProp } from 'helpers/types';
import Table from 'components/Table';
import { Field } from 'components/DownloadWidget/DownloadWidget';
import { formatDate } from 'helpers/date';

import GET_COSTCO_MEMBERSHIP_EXPERIMENT_METRICS from './CostcoMembershipExperimentMetrics.queries';
import styles from './CostcoMembershipExperimentMetrics.styles';

type Props = {
  classes: ClassProp<ReturnType<typeof styles>>;
};

type CostcoMembershipExperimentMetrics = {
  day: string;
  sales: number;
  totalMemberships: number;
  execOfferBase: number;
  execOfferA: number;
  execOfferB: number;
  goldOfferBase: number;
  goldOfferA: number;
  goldOfferB: number;
  outOfExperiment: number;
  cashbackAmount: number;
  campaignFeeAmount: number;
  marketingFeeAmount: number;
};

type QueryResponse = {
  costcoMembershipExperimentMetrics: CostcoMembershipExperimentMetrics[];
};

const getTableHeader = (merchantName = '') => [
  {
    source: ['day'],
    sortable: ['day'],
    label: 'Date',
    transform: (date: string) =>
      date !== '-' ? formatDate(date, 'MM/DD/YYYY') : 'Totals',
  },
  {
    source: ['execOfferBase'],
    sortable: ['execOfferBase'],
    justify: 'right',
    label: 'Executive Offer Base $20',
  },
  {
    source: ['execOfferA'],
    sortable: ['execOfferA'],
    justify: 'right',
    label: 'Exec Offer Group A $20',
  },
  {
    source: ['execOfferB'],
    sortable: ['execOfferB'],
    justify: 'right',
    label: 'Exec Offer Group B $20',
  },
  {
    source: ['goldOfferBase'],
    sortable: ['goldOfferBase'],
    justify: 'right',
    label: 'Gold Offer Base $20',
  },
  {
    source: ['goldOfferA'],
    sortable: ['goldOfferA'],
    justify: 'right',
    label: 'Gold Offer Group A $15',
  },
  {
    source: ['goldOfferB'],
    sortable: ['goldOfferB'],
    justify: 'right',
    label: 'Gold Offer Group B $10',
  },
  {
    source: ['totalMemberships'],
    sortable: ['totalMemberships'],
    justify: 'right',
    label: 'Total Memberships',
  },
  {
    source: ['sales'],
    sortable: ['sales'],
    justify: 'right',
    label: 'Total Revenue',
    transform: format('$,.0f'),
  },
  {
    source: ['cashbackAmount'],
    sortable: ['cashbackAmount'],
    justify: 'right',
    label: 'Cashback Amount',
    transform: format('$,.2f'),
  },
  {
    source: ['campaignFeeAmount'],
    sortable: ['campaignFeeAmount'],
    justify: 'right',
    label: 'Campaign Fee Amount',
    transform: format('$,.2f'),
  },
];

const getCSVFields = (merchantName = '') => [
  {
    label: 'Date',
    value: ({ day }: CostcoMembershipExperimentMetrics) =>
      day !== '-' ? formatDate(day, 'MM/DD/YYYY') : 'Totals',
  },
  {
    label: 'Executive Offer Base $20',
    value: 'execOfferBase',
  },
  {
    label: 'Exec Offer Group A $20',
    value: 'execOfferA',
  },
  {
    label: 'Exec Offer Group B $20',
    value: 'execOfferB',
  },
  {
    label: 'Gold Offer Base $20',
    value: 'goldOfferBase',
  },
  {
    label: 'Gold Offer Group A $15',
    value: 'goldOfferA',
  },
  {
    label: 'Gold Offer Group B $10',
    value: 'goldOfferB',
  },
  {
    label: 'Total Memberships',
    value: 'totalMemberships',
  },
  {
    label: 'Total Revenue',
    value: ({ sales }: CostcoMembershipExperimentMetrics) =>
      format('$,.2f')(sales),
  },
  {
    label: 'Cashback Amount',
    value: ({ cashbackAmount }: CostcoMembershipExperimentMetrics) =>
      format('$,.2f')(cashbackAmount),
  },
  {
    label: 'Campaign Fee Amount',
    value: ({ campaignFeeAmount }: CostcoMembershipExperimentMetrics) =>
      format('$,.2f')(campaignFeeAmount),
  },
];

const renderTable = (
  tableHeader: any,
  data: CostcoMembershipExperimentMetrics[],
  CSVFields: Field[],
) => (
  <Table
    sortDefaultColumn={{ name: 'Date', sortable: ['day'] }}
    tableHeader={tableHeader}
    data={data}
    splitEveryArg={25}
    showTotals
    exportData
    csvFields={CSVFields}
  />
);

const renderReport = (
  params: ReportParams,
  classes: ClassProp<ReturnType<typeof styles>>,
) => ({ costcoMembershipExperimentMetrics }: QueryResponse) => {
  const tableHeader = getTableHeader(params.merchantName);
  const CSVFields = getCSVFields(params.merchantName);

  return (
    <ReportBase header="Memberships Metrics" params={params}>
      <Grid.Row className={classes.charts} centered>
        <Grid.Column width={16} className={classes.gridTable}>
          <Header className={classes.tableHeader} as="h3">
            Totals (in store and online)
          </Header>

          <Header className={classes.tableHeader} as="h4">
            All totals include both cleared and pending transactions
          </Header>

          {!isEmpty(costcoMembershipExperimentMetrics) &&
            renderTable(
              tableHeader,
              costcoMembershipExperimentMetrics,
              CSVFields,
            )}
        </Grid.Column>
      </Grid.Row>
    </ReportBase>
  );
};

const CostcoMembershipsExperimentMetrics: FunctionComponent<Props> = ({
  classes,
}) => {
  const location = useLocation();
  const params = queryString.parse(location.search) as ReportParams;

  return (
    <Query
      query={GET_COSTCO_MEMBERSHIP_EXPERIMENT_METRICS}
      reportName="Costco Membership Experiment Metrics"
      queryKey="result"
      propName="costcoMembershipExperimentMetrics"
      checkForValidData={false}
      variables={{
        params,
      }}
      render={renderReport(params, classes)}
    />
  );
};

export default withStyles(styles as any)(CostcoMembershipsExperimentMetrics);
